import axios from 'axios'
import { v7 as uuidv7 } from 'uuid'

import AndroidHandler from 'utils/androidHandler'
import { LocalStorageSessionProvider } from '../auth/SessionProvider'
import { loadConfig } from '../config'
import {
  buildDefaultData,
  getDeviceInfoFromUA,
  getOrCreateDeviceId,
  getOrCreateSessionId,
  transformAttrs,
} from './utils'

import type { TransformAttrsArgs } from './utils'
import type { SatuEventConfig } from '../config'
import type { SessionProvider } from '../auth/types'

export interface TrackEventArgs {
  eventName: string
  eventParams?: Record<string, any>
  callback?: (args: { status: 'success' | 'failed'; data: any }) => void
  timeout?: number
  keepAlive?: boolean
}

export class SatuEventTracker {
  private static instance?: SatuEventTracker
  private config: SatuEventConfig
  private sessionProvider: SessionProvider

  private constructor(
    config?: SatuEventConfig,
    sessionProvider?: SessionProvider
  ) {
    this.config = config || loadConfig()
    this.sessionProvider =
      sessionProvider ||
      new LocalStorageSessionProvider(this.config.sessionStorageKey)
  }

  static getInstance(
    config?: SatuEventConfig,
    sessionProvider?: SessionProvider
  ) {
    if (!SatuEventTracker.instance) {
      SatuEventTracker.instance = new SatuEventTracker(config, sessionProvider)
    }
    return SatuEventTracker.instance
  }

  static resetInstance() {
    SatuEventTracker.instance = undefined
  }

  transformAttrs = transformAttrs

  trackEvent({
    eventName,
    eventParams,
    callback = () => {},
    timeout,
    keepAlive,
  }: TrackEventArgs) {
    const { enabled, debugEnabled, url, username, password } = this.config

    if (!enabled || !url) {
      if (debugEnabled) console.error('[SatuEvent] Tracker disabled')
      return
    }

    const modeUsername = username
    const modePassword = password

    const eventParamAttrs = eventParams?.attributes || []
    let params = { ...eventParams }

    /** Set default attributes */
    params.attributes = [...eventParamAttrs, ...this.getDefaultAttrs()]

    /** Set default data*/
    params = {
      ...buildDefaultData(
        // ID
        () => uuidv7(),
        // Timestamp
        () => new Date(),
        // Device ID
        () => getOrCreateDeviceId(() => uuidv7()),
        // User ID
        () => this.getUserData().user_id
      ),
      ...params,
    }

    if (debugEnabled) {
      // eslint-disable-next-line no-console
      console.log(
        '[SatuEvent_Debug] - [Not Sending Tracker]',
        eventName,
        JSON.stringify(params, null, 2)
      )
      return
    }

    const requestBody = {
      event_name: eventName,
      ...(Object.keys(params)?.length > 0
        ? { data: JSON.stringify(params) }
        : {}),
    }

    if (keepAlive) {
      if (typeof window === 'undefined') return

      const proxyPath = '/api/satu-event/track'
      const payload = JSON.stringify(requestBody)

      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.sendBeacon === 'function'
      ) {
        const blob = new Blob([payload], { type: 'application/json' })
        navigator.sendBeacon(proxyPath, blob)
        return
      }

      fetch(proxyPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        keepalive: true,
        body: payload,
      })
      return
    }

    axios
      .post(url, requestBody, {
        headers: {
          'Content-Type': 'application/json',
        },
        auth: {
          username: modeUsername,
          password: modePassword,
        },
        timeout,
      })
      .then((res) => callback?.({ status: 'success', data: res }))
      .catch((error) => {
        console.error(error)
        callback?.({ status: 'failed', data: error })
      })
  }

  private getDefaultAttrs() {
    const defaultAttrs: TransformAttrsArgs = {
      session_id: getOrCreateSessionId(() => uuidv7()),
      email: this.getUserData().user_email,
      is_login: [!!this.getUserData().user_id, 'bool'],
      url: typeof window !== 'undefined' ? window.location.href : '',
      referrer_url: typeof document !== 'undefined' ? document.referrer : '',
      ...this.getDeviceInformation(),
    }
    return transformAttrs(defaultAttrs)
  }

  private getDeviceInformation() {
    const androidHandler = AndroidHandler()
    return {
      platform: androidHandler?.enabled ? 'APP' : 'WEB',
      os: getDeviceInfoFromUA().os,
      application_version: androidHandler?.getVersion(),
      user_agent: navigator?.userAgent || '',
    }
  }

  private getUserData() {
    if (this.sessionProvider?.getUserData) {
      return this.sessionProvider.getUserData()
    }

    const session = this.sessionProvider?.getSession?.()
    return {
      user_id: session?.user?.id,
      user_email: session?.user?.email,
    }
  }
}
